/**
 * Media API - Transcript and Keyframe management for video/audio documents.
 *
 * SOTA 2026: Full bodycam review support with synced playback.
 */

import apiClient, { type RequestConfig } from './client';

const DOCUMENTS_ENDPOINT = '/documents';

// ============================================================
// Types
// ============================================================

export interface TranscriptSegment {
  id: string;
  index: number;
  start_time_ms: number;
  end_time_ms: number;
  start_formatted: string;
  end_formatted: string;
  text: string;
  word_count: number;
  speaker_label?: string;
  confidence?: number;
}

export interface TranscriptResponse {
  document_id: string;
  filename: string;
  segment_count: number;
  segments: TranscriptSegment[];
  full_text: string;
}

export interface Keyframe {
  id: string;
  frame_number: number;
  timestamp_ms: number;
  timestamp_formatted: string;
  r2_key: string;
  thumbnail_r2_key?: string;
  scene_change: boolean;
  extraction_type: 'interval' | 'scene_change';
  width?: number;
  height?: number;
  file_size_bytes?: number;
}

export interface KeyframeResponse {
  document_id: string;
  filename: string;
  total_keyframes: number;
  keyframes: Keyframe[];
}

export interface TranscriptionStatus {
  document_id: string;
  has_job: boolean;
  has_transcript?: boolean;
  job_id?: string;
  status?: string;
  progress?: number;
  message?: string;
  segments_created?: number;
  duration_seconds?: number;
  language?: string;
  cost_usd?: number;
  error?: string;
  created_at?: string;
  completed_at?: string;
}

export interface KeyframeExtractionStatus {
  document_id: string;
  has_job: boolean;
  keyframe_count: number;
  job_id?: string;
  status?: string;
  progress?: number;
  message?: string;
  total_frames?: number;
  interval_frames?: number;
  scene_change_frames?: number;
  video_duration_seconds?: number;
  total_bytes_stored?: number;
  error?: string;
  created_at?: string;
  completed_at?: string;
}

export interface QueueTranscriptionResponse {
  message: string;
  document_id: string;
  job_id: string;
  status: string;
}

export interface QueueKeyframeExtractionResponse {
  message: string;
  document_id: string;
  job_id: string;
  status: string;
  config: {
    interval_seconds: number;
    include_scene_changes: boolean;
    generate_thumbnails: boolean;
  };
}

// Clip Types
export interface ClipCreateRequest {
  start_ms: number;
  end_ms: number;
  format?: 'mp4' | 'webm' | 'mp3' | 'wav';
  include_video?: boolean;
  include_audio?: boolean;
}

export interface ClipProvenance {
  source_document_id: string;
  source_sha256?: string;
  source_filename?: string;
  source_mime_type?: string;
  clip_sha256?: string;
  timestamp_range_ms: [number, number];
  extraction_engine: string;
  extraction_engine_version?: string;
  extracted_at?: string;
  extracted_by?: string;
}

export interface ClipResponse {
  success: boolean;
  clip_id?: string;
  document_id: string;
  start_time_ms: number;
  end_time_ms: number;
  duration_ms: number;
  format: string;
  file_size_bytes?: number;
  download_url?: string;
  provenance?: ClipProvenance;
  error?: string;
}

// Waveform Types
export interface WaveformGenerateRequest {
  width?: number;
  height?: number;
  color?: string;
  include_peaks?: boolean;
}

export interface WaveformResponse {
  success: boolean;
  document_id: string;
  waveform_url?: string;
  width?: number;
  height?: number;
  duration_seconds?: number;
  peaks?: number[];
  error?: string;
}

export interface WaveformPeaksResponse {
  document_id: string;
  peaks: number[];
  duration_seconds: number;
  sample_rate: number;
  samples_per_peak: number;
}

// Bookmark Types
export interface MediaBookmark {
  id: string;
  document_id: string;
  user_id: string;
  timestamp_ms: number;
  end_timestamp_ms?: number;
  timestamp_formatted: string;
  segment_id?: string;
  keyframe_id?: string;
  label: string;
  notes?: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface BookmarkListResponse {
  document_id: string;
  bookmarks: MediaBookmark[];
  total: number;
}

export interface BookmarkCreateRequest {
  timestamp_ms: number;
  end_timestamp_ms?: number;
  segment_id?: string;
  keyframe_id?: string;
  label?: string;
  notes?: string;
  color?: string;
}

export interface BookmarkUpdateRequest {
  label?: string;
  notes?: string;
  color?: string;
  timestamp_ms?: number;
  end_timestamp_ms?: number;
}

// ============================================================
// API Methods
// ============================================================

export const mediaApi = {
  /**
   * Get transcript segments for a document.
   * Returns timestamped segments with speaker labels.
   */
  async getTranscript(
    documentId: string,
    config?: RequestConfig
  ): Promise<TranscriptResponse> {
    const { data } = await apiClient.get<TranscriptResponse>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/transcript`,
      config
    );
    return data;
  },

  /**
   * Get keyframes for a video document.
   * Supports filtering by scene changes only.
   */
  async getKeyframes(
    documentId: string,
    options?: {
      scene_changes_only?: boolean;
      limit?: number;
      offset?: number;
    },
    config?: RequestConfig
  ): Promise<KeyframeResponse> {
    const { data } = await apiClient.get<KeyframeResponse>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/keyframes`,
      {
        ...config,
        params: options as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get transcription job status.
   */
  async getTranscriptionStatus(
    documentId: string,
    config?: RequestConfig
  ): Promise<TranscriptionStatus> {
    const { data } = await apiClient.get<TranscriptionStatus>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/transcription-status`,
      config
    );
    return data;
  },

  /**
   * Get keyframe extraction job status.
   */
  async getKeyframeStatus(
    documentId: string,
    config?: RequestConfig
  ): Promise<KeyframeExtractionStatus> {
    const { data } = await apiClient.get<KeyframeExtractionStatus>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/keyframe-status`,
      config
    );
    return data;
  },

  /**
   * Queue transcription job for a document.
   * Supports audio and video files.
   */
  async queueTranscription(
    documentId: string,
    config?: RequestConfig
  ): Promise<QueueTranscriptionResponse> {
    const { data } = await apiClient.post<QueueTranscriptionResponse>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/transcribe`,
      {},
      config
    );
    return data;
  },

  /**
   * Queue keyframe extraction job for a video.
   */
  async queueKeyframeExtraction(
    documentId: string,
    options?: {
      interval?: number;
      include_scene_changes?: boolean;
      generate_thumbnails?: boolean;
    },
    config?: RequestConfig
  ): Promise<QueueKeyframeExtractionResponse> {
    const params = new URLSearchParams();
    if (options?.interval !== undefined) {
      params.set('interval', String(options.interval));
    }
    if (options?.include_scene_changes !== undefined) {
      params.set('include_scene_changes', String(options.include_scene_changes));
    }
    if (options?.generate_thumbnails !== undefined) {
      params.set('generate_thumbnails', String(options.generate_thumbnails));
    }

    const { data } = await apiClient.post<QueueKeyframeExtractionResponse>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/extract-keyframes?${params.toString()}`,
      {},
      config
    );
    return data;
  },

  /**
   * Get keyframe image URL via artifact download endpoint.
   * Pass the artifact_id from keyframe metadata (not the r2_key).
   */
  getKeyframeUrl(documentId: string, artifactId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    return `${baseUrl}/api/documents/${documentId}/artifacts/${encodeURIComponent(artifactId)}/download`;
  },

  /**
   * Get keyframe thumbnail URL via artifact download endpoint.
   * Falls back to the full keyframe image if no thumbnail artifact exists.
   */
  getThumbnailUrl(documentId: string, thumbnailArtifactId: string): string {
    const baseUrl = import.meta.env.VITE_API_URL || '';
    return `${baseUrl}/api/documents/${documentId}/artifacts/${encodeURIComponent(thumbnailArtifactId)}/download`;
  },

  /**
   * Create a clip from a video/audio document.
   */
  async createClip(
    documentId: string,
    request: ClipCreateRequest,
    config?: RequestConfig
  ): Promise<ClipResponse> {
    const { data } = await apiClient.post<ClipResponse>(
      `/clips/documents/${documentId}/clips`,
      request,
      config
    );
    return data;
  },

  /**
   * Generate waveform visualization for a document.
   */
  async generateWaveform(
    documentId: string,
    options?: WaveformGenerateRequest,
    config?: RequestConfig
  ): Promise<WaveformResponse> {
    const { data } = await apiClient.post<WaveformResponse>(
      `/clips/documents/${documentId}/waveform`,
      options || {},
      config
    );
    return data;
  },

  /**
   * Get waveform peak data for client-side rendering.
   */
  async getWaveformPeaks(
    documentId: string,
    config?: RequestConfig
  ): Promise<WaveformPeaksResponse> {
    const { data } = await apiClient.get<WaveformPeaksResponse>(
      `/clips/documents/${documentId}/waveform/peaks`,
      config
    );
    return data;
  },

  // ============================================================
  // Bookmark Methods
  // ============================================================

  /**
   * List bookmarks for a document (current user's bookmarks).
   */
  async listBookmarks(
    documentId: string,
    config?: RequestConfig
  ): Promise<BookmarkListResponse> {
    const { data } = await apiClient.get<BookmarkListResponse>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/bookmarks`,
      config
    );
    return data;
  },

  /**
   * Create a bookmark on a media document.
   */
  async createBookmark(
    documentId: string,
    request: BookmarkCreateRequest,
    config?: RequestConfig
  ): Promise<MediaBookmark> {
    const { data } = await apiClient.post<MediaBookmark>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/bookmarks`,
      request,
      config
    );
    return data;
  },

  /**
   * Update an existing bookmark.
   */
  async updateBookmark(
    documentId: string,
    bookmarkId: string,
    request: BookmarkUpdateRequest,
    config?: RequestConfig
  ): Promise<MediaBookmark> {
    const { data } = await apiClient.put<MediaBookmark>(
      `${DOCUMENTS_ENDPOINT}/${documentId}/bookmarks/${bookmarkId}`,
      request,
      config
    );
    return data;
  },

  /**
   * Delete a bookmark.
   */
  async deleteBookmark(
    documentId: string,
    bookmarkId: string,
    config?: RequestConfig
  ): Promise<void> {
    await apiClient.delete(
      `${DOCUMENTS_ENDPOINT}/${documentId}/bookmarks/${bookmarkId}`,
      config
    );
  },
};

export default mediaApi;
